/**
 * Curated feature vocabulary for "what do you have with solar" style queries.
 *
 * Two hard rules keep this from swamping results:
 *  1. Only phrases from this list count — free-text description matching would
 *     tag nearly every unit (marketing copy mentions everything).
 *  2. "prep"/"ready"/"optional" wording is NOT the feature. 70 of 76 units say
 *     "solar prep wiring"; only units with a real panel/package are tagged.
 */

export interface FeatureDef {
  id: string;
  label: string;
  /** Query wording a visitor might type. */
  query: string[];
  /** Positive matchers run against the unit's feature text. */
  match: RegExp[];
}

/** Wording that means the feature is only pre-wired / optional, not installed. */
const PREP_WORDS = /(prep|prepped|pre[\s-]?wir|ready|optional|available|upgrade|order)/i;

export const FEATURE_DEFS: FeatureDef[] = [
  {
    id: "solar",
    label: "Solar power",
    query: ["solar", "solar panel", "solar panels", "solar power", "solar package"],
    match: [/\bsolar\s+(panel|package|system|charg|kit)/i, /\b\d{2,4}\s*w(att)?s?\s+solar/i, /\bsolar\b[^.]*\bcontroller\b/i],
  },
  {
    id: "generator",
    label: "Generator",
    query: ["generator", "onan", "genset"],
    match: [/\b(onan|generator|genset)\b/i],
  },
  {
    id: "inverter",
    label: "Power inverter",
    query: ["inverter", "battery inverter"],
    match: [/\b\d{3,4}\s*w(att)?\s+inverter\b/i, /\b(pure sine|power)\s+inverter\b/i, /\binverter\b/i],
  },
  {
    id: "outdoor-kitchen",
    label: "Outdoor kitchen",
    query: ["outdoor kitchen", "outside kitchen", "exterior kitchen"],
    match: [/\b(outdoor|outside|exterior)\s+kitchen\b/i, /\b(outdoor|exterior)\s+(griddle|grill)\b/i],
  },
  {
    id: "bunkhouse",
    label: "Bunkhouse / bunk beds",
    query: ["bunkhouse", "bunk house", "bunks", "bunk beds", "bunk bed"],
    match: [/\bbunk\s?(house|beds?|room)\b/i, /\b(double|triple|quad)\s+bunks\b/i],
  },
  {
    id: "king-bed",
    label: "King bed",
    query: ["king bed", "king size bed", "king"],
    match: [/\bking(?:[\s-]size)?\s+bed\b/i, /\bking\s+bed\b/i],
  },
  {
    id: "murphy-bed",
    label: "Murphy bed",
    query: ["murphy bed", "murphy"],
    match: [/\bmurphy\s+bed\b/i],
  },
  {
    id: "fireplace",
    label: "Fireplace",
    query: ["fireplace", "electric fireplace"],
    match: [/\bfireplace\b/i],
  },
  {
    id: "theater-seating",
    label: "Theater seating",
    query: ["theater seating", "theatre seating", "recliners", "theater seats"],
    match: [/\btheat(er|re)\s+seat/i, /\bdual\s+recliners?\b/i],
  },
  {
    id: "kitchen-island",
    label: "Kitchen island",
    query: ["kitchen island", "island kitchen", "island"],
    match: [/\bkitchen\s+island\b/i, /\bisland\s+kitchen\b/i],
  },
  {
    id: "washer-dryer",
    label: "Washer / dryer",
    query: ["washer dryer", "washer and dryer", "laundry", "washer"],
    match: [/\bwasher\s*(?:\/|and|&)?\s*dryer\b/i],
  },
  {
    id: "dishwasher",
    label: "Dishwasher",
    query: ["dishwasher"],
    match: [/\bdishwasher\b/i],
  },
  {
    id: "residential-fridge",
    label: "Residential refrigerator",
    query: ["residential fridge", "residential refrigerator", "big fridge"],
    match: [/\bresidential\s+(stainless\s+steel\s+)?(refrigerator|fridge)\b/i, /\b(12|16|18)\s*cu/i],
  },
  {
    id: "auto-leveling",
    label: "Auto leveling",
    query: ["auto leveling", "automatic leveling", "level up", "hydraulic leveling"],
    match: [/\b(auto(matic)?|hydraulic|level[\s-]?up)\b[^.]*\bleveling\b/i, /\bleveling\s+system\b/i],
  },
  {
    id: "four-season",
    label: "Four-season package",
    query: ["four season", "4 season", "all season", "cold weather", "heated underbelly"],
    match: [/\bfour[\s-]season\b/i, /\b4[\s-]season\b/i, /\bheated\s+and\s+enclosed\s+underbelly\b/i, /\benclosed,?\s+heated\s+underbelly\b/i],
  },
  {
    id: "dual-ac",
    label: "Two air conditioners",
    query: ["dual ac", "two ac", "second ac", "dual air conditioners", "2 ac"],
    match: [/\bdual\s+(15|13\.5|15,000|13,500)?\s*(btu\s*)?(units?|a\/?c|air\s+conditioner)/i, /\btwo\s+air\s+conditioner/i, /\bsecond\s+a\/?c\b/i],
  },
  {
    id: "outdoor-shower",
    label: "Outside shower",
    query: ["outdoor shower", "outside shower", "exterior shower"],
    match: [/\b(outdoor|outside|exterior)\s+shower\b/i],
  },
  {
    id: "backup-camera",
    label: "Backup camera",
    query: ["backup camera", "back up camera", "rear camera", "rear view camera"],
    match: [/\b(back[\s-]?up|backup|rear[\s-]?view|rear)\s+(\/?\s?sideview\s+)?camera/i],
  },
  {
    id: "power-awning",
    label: "Power awning",
    query: ["power awning", "electric awning", "dual awnings"],
    match: [/\b(power|electric)\s+awning/i, /\bdual\s+electric\s+awnings\b/i],
  },
  {
    id: "half-bath",
    label: "Bath and a half",
    query: ["bath and a half", "half bath", "two bathrooms", "1.5 bath"],
    match: [/\bbath\s+and\s+a\s+half\b/i, /\bhalf\s+bath\b/i, /\b1\.5\s+bath/i],
  },
  {
    id: "walk-in-closet",
    label: "Walk-in closet",
    query: ["walk in closet", "walk-in closet"],
    match: [/\bwalk[\s-]?in\s+closet\b/i],
  },
  {
    id: "fifty-amp",
    label: "50 amp service",
    query: ["50 amp", "50amp", "fifty amp"],
    match: [/\b50[\s-]?amp\b/i],
  },
];

const FEATURE_BY_ID = new Map(FEATURE_DEFS.map((f) => [f.id, f]));

export function featureLabel(id: string): string {
  return FEATURE_BY_ID.get(id)?.label ?? id;
}

/** A token form that survives index tokenization (letters/digits only). */
export function featureToken(id: string): string {
  return `xfeat${id.replace(/[^a-z0-9]/g, "")}`;
}

/**
 * Tag a unit from its own feature text. Sentences that only advertise a "prep"
 * or optional package are skipped, so "solar prep wiring" is not solar.
 */
export function extractFeatureTags(parts: (string | null | undefined)[]): string[] {
  const sentences = parts
    .filter(Boolean)
    .flatMap((p) => String(p).split(/[\n.;•|]+|(?<=\))\s{2,}/))
    .map((s) => s.trim())
    .filter((s) => s.length > 2);
  const tags = new Set<string>();
  for (const def of FEATURE_DEFS) {
    for (const s of sentences) {
      // Feed blobs are often one long unpunctuated line listing dozens of
      // features, so a prep word anywhere in it must not veto an unrelated
      // real feature. Only the wording immediately around the match counts.
      let matched = false;
      for (const re of def.match) {
        const local = new RegExp(re.source, re.flags.includes("g") ? re.flags : re.flags + "g");
        local.lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = local.exec(s)) !== null) {
          const start = Math.max(0, m.index - 40);
          const window = s.slice(start, m.index + m[0].length + 40);
          if (!PREP_WORDS.test(window)) {
            matched = true;
            break;
          }
          if (m.index === local.lastIndex) local.lastIndex++;
        }
        if (matched) break;
      }
      if (matched) {
        tags.add(def.id);
        break;
      }
    }
  }
  return [...tags];
}

/**
 * Read requested features out of a visitor query and remove the matched wording
 * so it never becomes a required title token.
 */
export function detectFeatureRequests(text: string): { tags: string[]; text: string } {
  let out = ` ${text} `;
  const tags = new Set<string>();
  const phrases = FEATURE_DEFS.flatMap((d) => d.query.map((q) => ({ id: d.id, q: q.toLowerCase() })))
    .sort((a, b) => b.q.length - a.q.length);
  for (const { id, q } of phrases) {
    if (tags.has(id)) continue;
    const needle = ` ${q} `;
    if (out.includes(needle)) {
      tags.add(id);
      out = out.split(needle).join(" ");
    }
  }
  return { tags: [...tags], text: out.replace(/\s+/g, " ").trim() };
}
